/**
 * Stripe Preloading
 * Preloads Stripe.js and configuration in the background for faster payment display.
 *
 * The module-level cache is keyed by deviceId. Different devices can belong to
 * different organizations with different Stripe Connect accounts, and a Stripe.js
 * instance is bound to one account at load time. Serving a cached instance for
 * the wrong device would pair it with a PaymentIntent created on another
 * connected account, breaking Apple Pay/Google Pay and card confirmation until
 * the user hard-refreshes.
 */
import { useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';

interface StripePreloadConfig {
  publishableKey: string;
  connectedAccountId?: string;
}

interface StripePreloadCache {
  deviceId: string;
  promise: Promise<Stripe | null>;
  config: StripePreloadConfig;
}

// Global cache for Stripe instance - survives component unmounts.
let globalStripeCache: StripePreloadCache | null = null;
// Latest device a preload was requested for — guards against an older in-flight
// config fetch overwriting the cache after the user switched device/org.
let latestRequestedDeviceId: string | null = null;

function setCache(deviceId: string, config: StripePreloadConfig): void {
  const stripeOptions = config.connectedAccountId
    ? { stripeAccount: config.connectedAccountId }
    : undefined;

  const cache: StripePreloadCache = {
    deviceId,
    config,
    promise: loadStripe(config.publishableKey, stripeOptions),
  };
  globalStripeCache = cache;

  // Evict a poisoned entry: if Stripe.js fails to load (offline blip,
  // blocked script), a permanently cached rejection would make every
  // subsequent checkout and "Försök igen" fail until a hard refresh.
  // Dropping the entry lets the next preload/init load fresh. This also
  // keeps the unconsumed-preload path free of unhandled rejections.
  cache.promise.then(
    (stripe) => {
      if (!stripe && globalStripeCache === cache) globalStripeCache = null;
    },
    () => {
      if (globalStripeCache === cache) globalStripeCache = null;
    },
  );
}

/**
 * Get the preloaded Stripe instance for a specific device.
 * Returns null if not preloaded, or if the cache belongs to another device.
 */
export function getPreloadedStripe(deviceId: string): Promise<Stripe | null> | null {
  return globalStripeCache?.deviceId === deviceId ? globalStripeCache.promise : null;
}

/**
 * Get the cached Stripe configuration for a specific device.
 * Returns null if not preloaded, or if the cache belongs to another device.
 */
export function getPreloadedStripeConfig(deviceId: string): StripePreloadConfig | null {
  return globalStripeCache?.deviceId === deviceId ? globalStripeCache.config : null;
}

/**
 * Preload Stripe.js and payment config for a device (low priority).
 * Uses requestIdleCallback to avoid blocking rendering. Safe to call
 * repeatedly — dedupes on the cache and drops superseded in-flight fetches.
 */
export function preloadStripeGlobal(deviceId: string | null) {
  if (!deviceId) return;

  // Always record the newest interest, even when the cache already matches —
  // otherwise an older in-flight fetch for another device would pass the
  // staleness check below and clobber the current device's valid cache.
  latestRequestedDeviceId = deviceId;

  if (globalStripeCache?.deviceId === deviceId) return;

  const doPreload = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('api-stripe', {
        body: { action: 'get-payment-config', deviceId }
      });

      if (error || !data?.publishableKey) return;
      // A newer preload was requested while this fetch was in flight — drop it.
      if (latestRequestedDeviceId !== deviceId) return;
      // A concurrent call already populated the cache for this device.
      if (globalStripeCache?.deviceId === deviceId) return;

      setCache(deviceId, {
        publishableKey: data.publishableKey,
        connectedAccountId: data.connectedAccountId || undefined,
      });
      console.log('[preloadStripeGlobal] Stripe.js preload started (idle)');
    } catch (err) {
      console.error('[preloadStripeGlobal] Failed:', err);
    }
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(doPreload);
  } else {
    doPreload();
  }
}

/**
 * Hook wrapper for pages (CartPage) that want the preload to follow a
 * reactive deviceId. Delegates to preloadStripeGlobal so there is exactly
 * one preload implementation and one cache-invariant to maintain.
 */
export function useStripePreload(deviceId: string | null) {
  useEffect(() => {
    preloadStripeGlobal(deviceId);
  }, [deviceId]);
}
